import React, { ReactNode, useCallback, useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { StyledComponent } from 'styled-components';
import { useTranslation } from 'react-i18next';

import { useLearningCollectionItemRoute } from '@/pages/learning/LearningCollection/LearningCollectionPage';
import { Provider, mapProviderLink } from '@/store/providers';
import { http } from '@/utils/lib/http';
import { config } from '@/config';
import { SharedSnackbarContext, SnackbarType } from '@/component/SharedSnackbar/SharedSnackbar';
import { useUserSelfTrackedLearningLogic } from '@/component/UserSelfTrackedLearning/hooks/useUserSelfTrackedLearningLogic';
import { trackLearningCardClicked } from '@/utils/tracking/learnings';

interface Props {
  children: ReactNode | ReactNode[];
  isApproved: boolean;
  link?: string;
  customLink?: string | null;
  provider?: Provider | null;
  ariaLabel: string;
  isMandatory?: boolean;
  isCollection?: boolean;
  entityId: number;
  isApprovalRequired?: boolean;
  userSelfTrackedLearningId?: number;
  isCompletedPage?: boolean;
  Component: StyledComponent<any, any>;
  title: string;
  type: string;
  featured: boolean;
  isExternal?: boolean;
}

const canvasInstanceUrl = 'https://collegial.instructure.com';

export const CourseCardWrapper = ({
  children,
  isApproved,
  link,
  customLink,
  provider,
  ariaLabel,
  isMandatory = false,
  isCollection = false,
  entityId,
  isApprovalRequired,
  userSelfTrackedLearningId,
  title,
  type,
  featured,
  Component,
  isExternal,
}: Props): JSX.Element => {
  let linkToUse: string | undefined = useLearningCollectionItemRoute(isExternal ? '' : link);
  let isInternalLink = true;
  const { openSnackbar } = useContext(SharedSnackbarContext);
  const { t } = useTranslation('auth');
  const { openUserSelfTrackedLearningForm } = useUserSelfTrackedLearningLogic();

  const baseComponentProps = {
    $isMandatory: isMandatory,
    $isCollection: isCollection,
    'aria-label': ariaLabel,
  };

  const [isCanvasLink, setIsCanvasLink] = useState(false);
  const handleCanvasCourse = useCallback(async () => {
    try {
      const data = (await http.get(`${config.baseApiRoute}/provider/canvas/${entityId}`)) as { redirectUrl: string };
      window.location.href = data.redirectUrl;
    } catch {
      openSnackbar({ type: SnackbarType.DANGER, message: t('canvas-error') });
    }
  }, []);

  useEffect(() => {
    if (customLink && customLink.includes(canvasInstanceUrl)) {
      setIsCanvasLink(true);
    }
  }, [customLink]);

  // link non-curated Coursera & Pluralsight content directly to Coursera (even if they do not have access)
  if (!linkToUse && !isCanvasLink && provider && customLink) {
    linkToUse = mapProviderLink(provider, customLink);
    isInternalLink = false;
  }

  if (userSelfTrackedLearningId) {
    return (
      <Component
        onClick={() => {
          openUserSelfTrackedLearningForm(userSelfTrackedLearningId);
          trackLearningCardClicked(title, type, featured);
        }}
      >
        {children}
      </Component>
    );
  }

  if ((isCanvasLink && isApproved && isApprovalRequired) || (isCanvasLink && !isApprovalRequired)) {
    return (
      <Component
        {...baseComponentProps}
        onClick={() => {
          handleCanvasCourse();
          trackLearningCardClicked(title, type, featured);
        }}
      >
        {children}
      </Component>
    );
  } else if ((isInternalLink && linkToUse) || (linkToUse && isCanvasLink)) {
    return (
      <Component {...baseComponentProps} as={Link} to={linkToUse} onClick={() => trackLearningCardClicked(title, type, featured)}>
        {children}
      </Component>
    );
  } else if (!isInternalLink) {
    return (
      <Component
        {...baseComponentProps}
        as="a"
        href={linkToUse}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackLearningCardClicked(title, type, featured)}
      >
        {children}
      </Component>
    );
  }

  return (
    <Component {...baseComponentProps} onClick={() => trackLearningCardClicked(title, type, featured)}>
      {children}
    </Component>
  );
};
